import React, { useEffect, useMemo, useState } from "react";
import { CheckedInAction } from "src/components/CheckedIn/CheckedInSiteStyles";
import { CheckedInIcon } from "src/components/CheckedIn/CheckedInIcon";
import { ViewerBar, ViewerMessage } from "src/components/CheckedIn/CheckedInContentStyles";
import { articleHtml, articleLink } from "src/components/CheckedIn/articleHtml";
import { getQuoteArticle, QuoteArticle } from "./quotesApi";
import { QuoteArticleContent } from "./QuotesViewerStyles";

export function QuoteArticleView({ uuid, onClose }: { uuid: string; onClose: () => void }): React.ReactElement {
    const [article, setArticle] = useState<QuoteArticle | undefined>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [retry, setRetry] = useState<number>(0);
    const [imageFailed, setImageFailed] = useState<boolean>(false);
    const html: string = useMemo(() => articleHtml(article?.articleHtml || ""), [article?.articleHtml]);
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(false);
        setImageFailed(false);
        void getQuoteArticle(uuid, controller.signal).then(data => {
            if (!controller.signal.aborted) {
                setArticle(data);
                setLoading(false);
            }
        }).catch(() => {
            if (!controller.signal.aborted) {
                setError(true);
                setLoading(false);
            }
        });
        return () => controller.abort();
    }, [uuid, retry]);
    return <>
        <ViewerBar><span /><CheckedInAction type="button" $round aria-label="Close article" onClick={onClose}>
            <CheckedInIcon name="close" /></CheckedInAction></ViewerBar>
        {loading ? <ViewerMessage role="status">Loading article…</ViewerMessage>
            : error ? <ViewerMessage $error role="alert">We couldn’t load this article.
                <div><CheckedInAction type="button" onClick={() => setRetry(value => value + 1)}>Try again</CheckedInAction></div>
            </ViewerMessage> : article ? <QuoteArticleContent>
                <h1>{article.title}</h1>
                <div dangerouslySetInnerHTML={{ __html: html }} />
                {article.referenceLinks?.some(link => articleLink(link)) && <ul aria-label="Article references">
                    {article.referenceLinks.map(link => {
                        const href: string | undefined = articleLink(link);
                        return href ? <li key={link}><a href={href} target="_blank" rel="noopener noreferrer">{link}</a></li> : null;
                    })}
                </ul>}
                {article.imageUrl && !imageFailed && <img src={article.imageUrl} alt={article.title}
                    onError={() => setImageFailed(true)} />}
            </QuoteArticleContent> : <ViewerMessage role="status">This article is no longer available.</ViewerMessage>}
    </>;
}
